import * as Sentry from '@sentry/react';

// FullStory/Hotjar (loaded in public/index.html) rewrite DOM nodes behind React's back,
// so React's own removeChild/insertBefore calls throw NotFoundError mid-reconciliation
// (react/issues/11538, PD-946). Guard both so a stale third-party mutation degrades
// instead of crashing the whole tree.
//
// Both guards trade a loud crash for a silent recovery, which would also swallow
// any *unrelated* removeChild/insertBefore parent mismatch elsewhere in the app.
// A breadcrumb is recorded whenever a guard fires so that trail is still visible
// on the next captured Sentry event, instead of disappearing entirely.
export function patchReactDomConflicts() {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      Sentry.addBreadcrumb({
        category: 'dom-patch',
        message: 'removeChild: child.parentNode !== this, skipping removal',
        level: 'warning',
      });
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // referenceNode's original position is unrecoverable here, so this may
      // insert newNode in the wrong position rather than exactly where React
      // intended — best-effort recovery, not a true fix.
      Sentry.addBreadcrumb({
        category: 'dom-patch',
        message: 'insertBefore: referenceNode.parentNode !== this, falling back to appendChild',
        level: 'warning',
      });
      return originalAppendChild.call(this, newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}
